import axios from "axios";

import axiosInstance from "../../../api/axiosConfig";
import { agentBase } from "../api/agentApi";
import type { AgentScope } from "../types";
import type { AgentAttachment, CreateAttachmentResponse } from "./types";

const base = (scope: AgentScope): string => `${agentBase(scope)}/attachments`;
const one = (scope: AgentScope, id: string): string =>
  `${base(scope)}/${encodeURIComponent(id)}`;

export const AgentAttachmentsAPI = {
  async create(
    scope: AgentScope,
    file: { name: string; type: string; size: number }
  ): Promise<CreateAttachmentResponse> {
    const { data } = await axiosInstance.post<CreateAttachmentResponse>(
      base(scope),
      {
        filename: file.name,
        content_type: file.type || null,
        size_bytes: file.size
      }
    );
    return data;
  },
  async complete(scope: AgentScope, id: string): Promise<AgentAttachment> {
    const { data } = await axiosInstance.post<{ attachment: AgentAttachment }>(
      `${one(scope, id)}/complete`
    );
    return data.attachment;
  },
  async get(scope: AgentScope, id: string): Promise<AgentAttachment> {
    const { data } = await axiosInstance.get<{ attachment: AgentAttachment }>(
      one(scope, id)
    );
    return data.attachment;
  },
  /** Only an attachment not yet sent can be removed. */
  async remove(scope: AgentScope, id: string): Promise<void> {
    await axiosInstance.delete(one(scope, id));
  },
  /** The bytes, through the API (it needs the auth header, so no plain `<img src>`). */
  async content(
    scope: AgentScope,
    id: string,
    options?: { download?: boolean; signal?: AbortSignal }
  ): Promise<Blob> {
    const { data } = await axiosInstance.get<Blob>(
      `${one(scope, id)}/content`,
      {
        responseType: "blob",
        params: options?.download ? { download: 1 } : undefined,
        signal: options?.signal
      }
    );
    return data;
  },
  /**
   * Upload `file`: open the attachment, PUT the bytes to its resumable session
   * (progress 0–1), then mark it uploaded.
   */
  async upload(
    scope: AgentScope,
    file: File,
    options?: {
      onCreated?: (attachment: AgentAttachment) => void;
      onProgress?: (fraction: number) => void;
      signal?: AbortSignal;
    }
  ): Promise<AgentAttachment> {
    const { attachment, upload_url } = await AgentAttachmentsAPI.create(
      scope,
      file
    );
    options?.onCreated?.(attachment);
    await axios.put(upload_url, file, {
      headers: { "Content-Type": attachment.content_type },
      signal: options?.signal,
      onUploadProgress: (event) => {
        const total = event.total ?? file.size;
        options?.onProgress?.(total ? event.loaded / total : 0);
      }
    });
    return AgentAttachmentsAPI.complete(scope, attachment.id);
  }
};
